import React from "react";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import { Languages } from "lucide-react";
import { Localizer } from "@/main/react/Types";

/**
 * The scope of the /groups directory + search: every group, or only groups in the viewer's reading
 * languages. Mirrors the profile-page discussion toggle (UserDiscussionLanguageScope).
 */
export type GroupLanguageScope = "all" | "mine";

/**
 * Small "All groups / Your languages" selector shown above the groups directory list. Unlike the
 * profile discussion toggle (which defaults to all), the directory defaults to the viewer's own
 * languages; this control widens it back out to every language. Labels reuse the shared
 * profile.language-scope.* catalog strings.
 *
 * "mine" resolves to the viewer's whole reading-set on the backend, not the UI locale alone — see
 * designs/01-now/2026-07-29-your-languages-reading-set-scope-design.md.
 *
 * **`DISCOVERY-12` — the control must look applied when it is applied.** The directory arrives already
 * narrowed, and until 2026-08-17 the trigger rendered in exactly the same neutral style on "Your
 * languages" as on "All languages": an applied filter and no filter were visually identical, so a
 * visitor met a slice of the catalog they had not asked for and read it as the whole catalog. The
 * DEFAULT is a recorded decision and is not changed here — only its legibility. So the narrowing state
 * takes the primary tint the rest of the product uses for an engaged filter, and the wrapper carries
 * `data-language-scope-narrowed` so the distinction is assertable without pinning a class string.
 */
export function GroupLanguageScopeSelect({
    value,
    onChange,
    localizer,
}: {
    value: GroupLanguageScope;
    onChange: (value: GroupLanguageScope) => void;
    localizer: Localizer;
}) {
    const options = React.useMemo(
        () => [
            { key: "mine", value: "mine", label: localizer("profile.language-scope.mine"), icon: Languages },
            { key: "all", value: "all", label: localizer("profile.language-scope.all") },
        ],
        [localizer],
    );

    const isNarrowing = value === "mine";

    return (
        <span
            // A flex item is blockified whatever its own `display` says, so the plain `span` lays out
            // exactly as the bare control did — this wrapper carries the hook and the width floor.
            //
            // `EMPTY-STATES-19` — at 412px the label rendered as "Your langua", cut inside the word
            // with no ellipsis, while the section heading beside it kept ~60% of the row for a query
            // the search box above already shows in full. Both children could shrink and the control
            // lost. This wrapper is part of why: when the control was rewritten for `DISCOVERY-12` it
            // added a SECOND `min-w-0`, on top of the one `SimpleSelect` already puts on the trigger,
            // so the control became more shrinkable than when the row was filed.
            //
            // `shrink-0` is the fix: a flex item that cannot shrink keeps its intrinsic width, and
            // `size="sm"` keeps the trigger `w-auto`, so the value stops being squeezed. The heading
            // beside it already carries `min-w-0 truncate` and is the redundant one, so it takes the
            // truncation instead.
            //
            // `max-w-[60%]` is a guard rail, not the fix. `max-width` is applied after flex
            // resolution, so it binds even on a `shrink-0` item: without it a long enough label (the
            // Spanish "Todos los idiomas", or a longer locale still) could push the row wider than a
            // phone viewport and mint a horizontal scroll. With it, truncation returns only past 60%
            // of the row — and `SimpleSelect`'s own `min-w-0` plus its value-level `truncate` are
            // what make that last-resort clip land cleanly instead of overflowing. They are
            // load-bearing for this ceiling; do not tidy them away.
            className="shrink-0 max-w-[60%]"
            data-language-scope-narrowed={isNarrowing ? "true" : "false"}
            data-playwright-id="groups-language-scope"
        >
            <SimpleSelect
                size="sm"
                value={value}
                onValueChange={(next) => onChange(next === "all" ? "all" : "mine")}
                options={options}
                ariaLabel={localizer("groups.language-scope.aria")}
                dataPlaywrightId="groups-language-scope-select"
                triggerClassName={isNarrowing
                    ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/10"
                    : undefined}
            />
        </span>
    );
}

export default GroupLanguageScopeSelect;
